import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/card';
import { MapPin, Navigation, Car, User, Phone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const DriverLocationMap = ({ 
  rideId, 
  pickup, 
  dropoff,
  driverLocation, // From WebSocket updates
  backendUrl,
  onOpenChat 
}) => {
  const [driverInfo, setDriverInfo] = useState(null);
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);

  // Fetch driver info and location
  useEffect(() => {
    const fetchDriverInfo = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/location/driver/${rideId}`);
        setDriverInfo(response.data);
      } catch (error) {
        console.error('Error fetching driver info:', error);
      }
    };

    if (rideId) {
      fetchDriverInfo();
      // Poll every 10 seconds as fallback
      const interval = setInterval(fetchDriverInfo, 10000);
      return () => clearInterval(interval);
    }
  }, [rideId, backendUrl]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    try {
      // Check if Leaflet is available
      if (typeof window.L === 'undefined') {
        setMapError(true);
        return;
      }

      const L = window.L;
      const center = pickup ? [pickup.lat, pickup.lng] : [26.1224, -80.1373];
      
      mapInstanceRef.current = L.map(mapRef.current).setView(center, 14);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(mapInstanceRef.current);

      // Add pickup marker
      if (pickup) {
        const pickupIcon = L.divIcon({
          className: 'custom-marker',
          html: '<div style="background-color: #22c55e; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        });
        L.marker([pickup.lat, pickup.lng], { icon: pickupIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup('Pickup Location');
      }

      // Add dropoff marker
      if (dropoff) {
        const dropoffIcon = L.divIcon({
          className: 'custom-marker',
          html: '<div style="background-color: #ef4444; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        });
        L.marker([dropoff.lat, dropoff.lng], { icon: dropoffIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup('Dropoff Location');
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [pickup, dropoff]);

  // Update driver marker when location changes
  useEffect(() => {
    const location = driverLocation || driverInfo?.location;
    if (!location || !mapInstanceRef.current) return;

    try {
      const L = window.L;
      
      // Remove old marker
      if (driverMarkerRef.current) {
        mapInstanceRef.current.removeLayer(driverMarkerRef.current);
      }

      // Create car icon for driver
      const driverIcon = L.divIcon({
        className: 'driver-marker',
        html: `<div style="background-color: #f59e0b; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      });

      driverMarkerRef.current = L.marker([location.lat, location.lng], { icon: driverIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(driverInfo?.driver_name || 'Your Driver');

      // Pan to show driver
      mapInstanceRef.current.panTo([location.lat, location.lng]);
    } catch (error) {
      console.error('Error updating driver marker:', error);
    }
  }, [driverLocation, driverInfo]);

  if (mapError) {
    return (
      <Card className="p-4 border-2 border-amber-200 bg-amber-50">
        <p className="text-amber-700 text-sm text-center">Map could not be loaded</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-2 border-amber-300" data-testid="driver-location-map">
      {/* Driver Info Header */}
      {driverInfo && driverInfo.driver_name && (
        <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center overflow-hidden">
                {driverInfo.driver_photo ? (
                  <img 
                    src={driverInfo.driver_photo.startsWith('/api') ? `${backendUrl}${driverInfo.driver_photo}` : driverInfo.driver_photo} 
                    alt={driverInfo.driver_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={20} className="text-amber-600" />
                )}
              </div>
              <div>
                <p className="font-semibold text-sm">{driverInfo.driver_name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{driverInfo.vehicle_model || 'Vehicle'}</span>
                  {driverInfo.license_plate && (
                    <>
                      <span>•</span>
                      <span className="font-medium">{driverInfo.license_plate}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onOpenChat}
                className="h-8 px-2"
                data-testid="open-chat-btn"
              >
                <MessageCircle size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div 
        ref={mapRef} 
        className="h-48 w-full"
        style={{ minHeight: '200px' }}
      />

      {/* Legend */}
      <div className="p-2 bg-white border-t flex items-center justify-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span>Pickup</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span>Dropoff</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
          <span>Driver</span>
        </div>
      </div>
    </Card>
  );
};

export default DriverLocationMap;
